import { ValueFormatterParams } from "ag-grid-community";
import { CheckCircle2, XCircle, Activity, Clock, Timer } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Grid from "../../../../components/Grid";
import { ColumnDef, GridHandle } from "../../../../components/Grid/model/Grid";
import Info from "./Info";
import { AuditTabFilter, DashboardTotals, IAuditoriaEntradaSaida, ICancelaItem } from "./types/types";
import InfoIcon from "../../../../assets/images/infoIcon.svg";
import { useBreadcrumb } from "../../../../hooks/BreadCrumbContext";
import api from "../../../../services/api";

const DATA_PATH = "/listar/auditoriaEntradaSaidaVeiculos";

const AnimatedNumber: React.FC<{
  value: number;
  formatter?: (value: number) => string;
  className?: string;
}> = ({ value, formatter = (n) => String(Math.round(n)), className }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValueRef = useRef(value);

  useEffect(() => {
    const startValue = previousValueRef.current;
    const change = value - startValue;
    const duration = 650;
    const startTime = performance.now();
    let animationFrame = 0;

    const animate = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(startValue + change * easedProgress);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = value;
        setDisplayValue(value);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [value]);

  return <span className={className}>{formatter(displayValue)}</span>;
};

const safeParseJson = (val: any) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
};

const parseDateToTimestamp = (value?: any): number | null => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str || str === "null" || str === "undefined") return null;

  // Formato compacto YYYYMMDDHHmmss (ex: "20250728001111")
  if (/^\d{14}$/.test(str)) {
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10) - 1;
    const day = parseInt(str.substring(6, 8), 10);
    const hour = parseInt(str.substring(8, 10), 10);
    const minute = parseInt(str.substring(10, 12), 10);
    const second = parseInt(str.substring(12, 14), 10);
    const d = new Date(year, month, day, hour, minute, second);
    return isNaN(d.getTime()) ? null : d.getTime();
  }

  // Formato padrão ISO ou YYYY-MM-DD HH:mm:ss
  try {
    const d = new Date(str.includes("T") ? str : str.replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d.getTime();
  } catch {
    return null;
  }
};

const getTempoAberturaInSeconds = (dataAbertura?: any, dataRecebido?: any): string => {
  const tsAbertura = parseDateToTimestamp(dataAbertura);
  const tsRecebido = parseDateToTimestamp(dataRecebido);

  if (!tsAbertura || !tsRecebido) return "---";

  const diffSeconds = Math.round((tsAbertura - tsRecebido) / 1000);
  if (isNaN(diffSeconds)) return "---";

  return `${Math.max(0, diffSeconds)}s`;
};

const formatBodyDate = (value?: any): string => {
  if (!value) return "---";
  const str = String(value).trim();
  if (!str || str === "null" || str === "undefined") return "---";

  // Formato compacto YYYYMMDDHHmmss (ex: "20250728001111")
  if (/^\d{14}$/.test(str)) {
    const year = str.substring(0, 4);
    const month = str.substring(4, 6);
    const day = str.substring(6, 8);
    const hour = str.substring(8, 10);
    const minute = str.substring(10, 12);
    const second = str.substring(12, 14);
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  // Formato padrão YYYY-MM-DD HH:mm:ss ou ISO
  try {
    const d = new Date(str.includes("T") ? str : str.replace(" ", "T"));
    if (!isNaN(d.getTime())) {
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(d);
    }
  } catch {
    // fallback
  }

  return str;
};

const CAM_ID_TO_CANCELA_MAP: Record<string, string> = {
  "1": "Cancela 1",
  "4": "Cancela 2",
  "7": "Cancela 3",
  "9": "Cancela 3 Saída",
  "11": "Cancela 4",
  "12": "Cancela 5",
};

const CANCELA_FILTER_OPTIONS = Object.entries(CAM_ID_TO_CANCELA_MAP).map(
  ([value, label]) => ({ value, label })
);

const EntradaVeiculosAudit: React.FC = () => {
  const [selectedRow, setSelectedRow] = useState<IAuditoriaEntradaSaida | undefined>();
  const [isView, setIsView] = useState(false);
  const [activeTab, setActiveTab] = useState<AuditTabFilter>("abriu");
  const [dashboardRows, setDashboardRows] = useState<IAuditoriaEntradaSaida[]>([]);
  const [rows24h, setRows24h] = useState<IAuditoriaEntradaSaida[]>([]);
  const [cancelasMap, setCancelasMap] = useState<Record<string, string>>({});
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const gridRef = useRef<GridHandle>(null);
  const { addBreadcrumb } = useBreadcrumb();

  // Buscar lista de usuários para mapear id_usuario_historico -> Nome de Usuário
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.post("/listar/usuarios", {
          qtd_por_pagina: 500,
          order_by: "id_usuario",
          order_direction: "asc",
        }).catch(() => null);

        const userList = res?.data?.data || res?.data || [];
        const map: Record<string, string> = {};

        if (Array.isArray(userList)) {
          userList.forEach((u: any) => {
            const id = u.id_usuario ?? u.id;
            const name = u.nome || u.name || u.usuario;
            if (id != null && name) {
              map[String(id)] = name;
            }
          });
        }

        setUsersMap(map);
      } catch (e) {
        console.error("Erro ao carregar usuários:", e);
      }
    };

    fetchUsers();
  }, []);

  // Buscar lista de cancelas para mapear camId (iss_id_camera_dianteira/traseira/superior) -> Número da cancela (numero_cancela)
  useEffect(() => {
    const fetchCancelas = async () => {
      try {
        const [resGeral, resEntrada, resSaida] = await Promise.all([
          api.post("/listar/cancelas", { qtd_por_pagina: 200, order_by: "id_operacao_patio_cancela", order_direction: "asc" }).catch(() => null),
          api.post("/listar/cancelasEntrada", {}).catch(() => null),
          api.post("/listar/cancelasSaida", {}).catch(() => null),
        ]);

        const listGeral: ICancelaItem[] = resGeral?.data?.data || [];
        const listEntrada: ICancelaItem[] = Array.isArray(resEntrada?.data) ? resEntrada.data : (resEntrada?.data?.data || []);
        const listSaida: ICancelaItem[] = Array.isArray(resSaida?.data) ? resSaida.data : (resSaida?.data?.data || []);

        const allCancelas = [...listGeral, ...listEntrada, ...listSaida];
        const map: Record<string, string> = {};

        allCancelas.forEach((item) => {
          const num = item.numero_cancela;
          const label = num !== undefined && num !== null && num !== ""
            ? String(num).toLowerCase().includes("cancela")
              ? String(num)
              : `Cancela ${num}`
            : item.nome || item.descricao || (item.id_cancela ? `Cancela ${item.id_cancela}` : undefined);

          if (label) {
            if (item.iss_id_camera_dianteira != null && String(item.iss_id_camera_dianteira) !== "") {
              map[String(item.iss_id_camera_dianteira)] = label;
            }
            if (item.iss_id_camera_traseira != null && String(item.iss_id_camera_traseira) !== "") {
              map[String(item.iss_id_camera_traseira)] = label;
            }
            if (item.iss_id_camera_superior != null && String(item.iss_id_camera_superior) !== "") {
              map[String(item.iss_id_camera_superior)] = label;
            }
            if (item.camId != null && String(item.camId) !== "") {
              map[String(item.camId)] = label;
            }
            if (item.id_cancela != null && String(item.id_cancela) !== "") {
              map[String(item.id_cancela)] = label;
            }
          }
        });

        setCancelasMap(map);
      } catch (e) {
        console.error("Erro ao carregar cancelas:", e);
      }
    };

    fetchCancelas();
  }, []);

  // Fetch para carregar métricas do Dashboard (Geral vs 24 Horas)
  const fetchDashboardMetrics = useCallback(async () => {
    try {
      const now = new Date();
      const date24hAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const formatDateForApi = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      // 1. Métricas das últimas 24h
      const response24h = await api.post(DATA_PATH, {
        qtd_por_pagina: 1000,
        order_by: "data_historico",
        order_direction: "desc",
        data_inicial: formatDateForApi(date24hAgo),
        data_final: formatDateForApi(now),
      }).catch(() => null);

      // 2. Métricas Gerais (amostra geral ampla do histórico)
      const responseGeral = await api.post(DATA_PATH, {
        qtd_por_pagina: 1500,
        order_by: "id",
        order_direction: "desc",
      }).catch(() => null);

      setRows24h(response24h?.data?.data || []);
      setDashboardRows(responseGeral?.data?.data || []);
    } catch (error) {
      console.error("Erro ao carregar métricas de auditoria:", error);
      setDashboardRows([]);
      setRows24h([]);
    }
  }, []);

  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  // Cálculos do Dashboard
  const dashboardTotals = useMemo<DashboardTotals>(() => {
    let totalConcluidos = 0;
    let totalFalhas = 0;

    dashboardRows.forEach((row) => {
      const parsedOpen = safeParseJson(row.open_cancela);
      const statusVal = parsedOpen?.status;
      const isSuccess = statusVal === 200 || statusVal === "200";

      if (isSuccess) {
        totalConcluidos += 1;
      } else {
        totalFalhas += 1;
      }
    });

    let totalConcluidos24h = 0;
    let sumSeconds24h = 0;
    let countValidSeconds24h = 0;

    rows24h.forEach((row) => {
      const parsedOpen = safeParseJson(row.open_cancela);
      const statusVal = parsedOpen?.status;
      if (statusVal === 200 || statusVal === "200") {
        totalConcluidos24h += 1;
      }

      const parsedBody = safeParseJson(row.body);
      const rawDate = parsedBody?.dataHora || parsedBody?.data_hora || parsedBody?.data;
      const tsAbertura = parseDateToTimestamp(row.data_historico);
      const tsRecebido = parseDateToTimestamp(rawDate);

      if (tsAbertura && tsRecebido) {
        const diffSec = Math.round((tsAbertura - tsRecebido) / 1000);
        if (!isNaN(diffSec) && diffSec >= 0) {
          sumSeconds24h += diffSec;
          countValidSeconds24h += 1;
        }
      }
    });

    const totalRequisicoes = dashboardRows.length;
    const totalRequisicoes24h = rows24h.length;

    const percentualSucesso = totalRequisicoes > 0 ? (totalConcluidos / totalRequisicoes) * 100 : 0;
    const percentualSucesso24h = totalRequisicoes24h > 0 ? (totalConcluidos24h / totalRequisicoes24h) * 100 : 0;
    const mediaTempo24h = countValidSeconds24h > 0 ? sumSeconds24h / countValidSeconds24h : 0;

    return {
      totalRequisicoes,
      totalConcluidos,
      totalFalhas,
      percentualSucesso,
      totalRequisicoes24h,
      totalConcluidos24h,
      percentualSucesso24h,
      mediaTempo24h,
    };
  }, [dashboardRows, rows24h]);

  const customFilters = useMemo(() => {
    return {
      tab_filter: activeTab,
    };
  }, [activeTab]);

  useEffect(() => {
    gridRef.current?.refresh();
  }, [activeTab]);

  const getCancelaName = useCallback(
    (camId?: string | number) => {
      if (camId === undefined || camId === null || camId === "" || camId === "---") return "---";
      const key = String(camId).trim();
      if (CAM_ID_TO_CANCELA_MAP[key]) {
        return CAM_ID_TO_CANCELA_MAP[key];
      }
      if (cancelasMap[key]) {
        return cancelasMap[key];
      }
      return `Cancela ${key}`;
    },
    [cancelasMap]
  );

  const getUserName = useCallback(
    (idUsuario?: string | number, fallbackUserString?: string) => {
      if (fallbackUserString && fallbackUserString !== "---" && fallbackUserString !== "null") {
        return fallbackUserString;
      }
      if (idUsuario === undefined || idUsuario === null || idUsuario === "" || idUsuario === "---") {
        return "---";
      }
      const key = String(idUsuario).trim();
      if (usersMap[key]) {
        return usersMap[key];
      }
      return `Usuário #${key}`;
    },
    [usersMap]
  );

  const columns: ColumnDef[] = [
    {
      field: "id",
      headerName: "ID",
      filter: true,
      width: 120,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return `#${params.value}`;
        return "---";
      },
    },
    {
      field: "placa",
      headerName: "Placa",
      filter: true,
      width: 150,
      valueGetter: (params) => {
        const parsed = safeParseJson(params.data?.body);
        return parsed?.placa || parsed?.placa_dianteira || parsed?.placaDianteira || "---";
      },
    },
    {
      field: "data_recebido",
      fieldName: "dataHora",
      headerName: "Data/Hora Recebido",
      filter: true,
      width: 200,
      valueGetter: (params) => {
        const parsed = safeParseJson(params.data?.body);
        const rawDate = parsed?.dataHora || parsed?.data_hora || parsed?.data;
        return formatBodyDate(rawDate);
      },
    },
    {
      field: "cancela",
      fieldName: "cancela",
      headerName: "Cancela",
      filter: true,
      width: 180,
      filterParams: {
        selected: {
          isMultiple: false,
          data: CANCELA_FILTER_OPTIONS,
        },
      },
      valueGetter: (params) => {
        const parsed = safeParseJson(params.data?.body);
        const camId = parsed?.camId || parsed?.id_cancela;
        return getCancelaName(camId);
      },
    },
    {
      field: "status_cancela",
      headerName: "Status Abertura",
      filter: false,
      sortable: false,
      width: 160,
      valueGetter: (params) => {
        const parsed = safeParseJson(params.data?.open_cancela);
        const statusVal = parsed?.status;
        return statusVal === 200 || statusVal === "200" ? "Abriu" : "Não Abriu";
      },
      cellRenderer: (params: any) => {
        const isAbriu = params.value === "Abriu";
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
              isAbriu
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            {isAbriu ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            {params.value}
          </span>
        );
      },
    },
    {
      field: "data_historico",
      headerName: "Data/Hora de Abertura",
      filter: true,
      filterParams: {
        dateBetween: true,
      },
      width: 220,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return "---";
        try {
          return new Intl.DateTimeFormat("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }).format(new Date(String(params.value).replace(" ", "T")));
        } catch {
          return String(params.value);
        }
      },
    },
    {
      field: "tempo_abertura",
      headerName: "Tempo para Abertura",
      filter: false,
      sortable: false,
      width: 190,
      valueGetter: (params) => {
        const parsedBody = safeParseJson(params.data?.body);
        const rawDate = parsedBody?.dataHora || parsedBody?.data_hora || parsedBody?.data;
        return getTempoAberturaInSeconds(params.data?.data_historico, rawDate);
      },
      cellRenderer: (params: any) => {
        if (!params.value || params.value === "---") return "---";
        return (
          <span className="inline-flex items-center gap-1 font-mono font-semibold text-[#071B3A] bg-[#F1F5F9] border border-[#CBD5E1] px-2.5 py-0.5 rounded-md text-xs shadow-xs">
            {params.value}
          </span>
        );
      },
    },
    {
      field: "latencia_ms",
      headerName: "Latência (ms)",
      filter: false,
      sortable: true,
      width: 150,
      valueGetter: (params) => {
        const parsedOpen = safeParseJson(params.data?.open_cancela);
        const parsedResp = safeParseJson(params.data?.response);
        const parsedBody = safeParseJson(params.data?.body);

        const latency = parsedOpen?.latency_ms ?? parsedOpen?.latencia_ms ?? parsedResp?.latency_ms ?? parsedBody?.latency_ms;
        if (latency !== undefined && latency !== null && latency !== "") {
          return `${Number(latency)} ms`;
        }
        return "---";
      },
      cellRenderer: (params: any) => {
        if (!params.value || params.value === "---") return "---";
        return (
          <span className="inline-flex items-center gap-1 font-mono font-semibold text-[#1E1B4B] bg-[#EEF2FF] border border-[#C7D2FE] px-2.5 py-0.5 rounded-md text-xs shadow-xs">
            {params.value}
          </span>
        );
      },
    },
    {
      field: "usuario_modificacao",
      fieldName: "usuario",
      headerName: "Usuário de Modificação",
      filter: true,
      width: 210,
      valueGetter: (params) => {
        // 1. Se o nome do usuário foi retornado do subselect/join na tabela usuarios do backend
        if (params.data?.usuario && typeof params.data.usuario === "string") {
          return params.data.usuario;
        }

        const parsedBody = safeParseJson(params.data?.body);

        // 2. Se o body já contiver a string do nome do usuário
        if (parsedBody?.usuario && typeof parsedBody.usuario === "string") {
          return parsedBody.usuario;
        }
        if (parsedBody?.nome_usuario && typeof parsedBody.nome_usuario === "string") {
          return parsedBody.nome_usuario;
        }

        // 3. Mapeamento via id_usuario_historico na tabela de usuários (/listar/usuarios)
        const idUser = parsedBody?.id_usuario_historico || parsedBody?.id_usuario || params.data?.id_usuario_historico;
        return getUserName(idUser);
      },
    },
  ];

  return (
    <>
      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Auditoria Entrada/Saída Veículos"
          cancelaName={selectedRow ? getCancelaName(safeParseJson(selectedRow.body)?.camId) : undefined}
          onClose={() => setIsView(false)}
        />
      )}

      <div className="flex flex-col w-full h-[calc(100dvh-4rem)] bg-[#F5F7FA] p-4 sm:p-6 overflow-y-auto">
        {/* Cabeçalho */}
        <div className="flex flex-col gap-2 mb-5">
          <h1 className="text-2xl text-[#071B3A] font-bold">
            Auditoria Entrada e Saída de Veículos
          </h1>
          <p className="text-sm text-[#6B7280]">
            Métricas de abertura de cancela e registro de logs de entrada/saída de veículos.
          </p>
        </div>

        {/* Dashboard Cards (Estilo Caixa Geral - 5 Cards) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Card 1: Taxa de Sucesso Geral */}
          <div className="rounded-xl bg-[#062D4E] p-5 text-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#FFD85A]">
                Taxa de Sucesso (Geral)
              </span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-[#FFD85A]">
                <Activity size={18} />
              </span>
            </div>
            <div className="mt-4">
              <strong className="block text-3xl font-bold leading-tight">
                <AnimatedNumber
                  value={dashboardTotals.percentualSucesso}
                  formatter={(val) => `${val.toFixed(1)}%`}
                />
              </strong>
              <span className="mt-2 block text-xs text-[#9FE6C4]">
                <AnimatedNumber value={dashboardTotals.totalConcluidos} /> de{" "}
                <AnimatedNumber value={dashboardTotals.totalRequisicoes} /> aberturas bem-sucedidas
              </span>
            </div>
          </div>

          {/* Card 2: Taxa de Sucesso (Últimas 24h) */}
          <div className="rounded-xl bg-[#0F3C66] p-5 text-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#38BDF8]">
                Taxa de Sucesso (24h)
              </span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-[#38BDF8]">
                <Clock size={18} />
              </span>
            </div>
            <div className="mt-4">
              <strong className="block text-3xl font-bold leading-tight text-[#38BDF8]">
                <AnimatedNumber
                  value={dashboardTotals.percentualSucesso24h}
                  formatter={(val) => `${val.toFixed(1)}%`}
                />
              </strong>
              <span className="mt-2 block text-xs text-[#7DD3FC]">
                <AnimatedNumber value={dashboardTotals.totalConcluidos24h} /> de{" "}
                <AnimatedNumber value={dashboardTotals.totalRequisicoes24h} /> aberturas nas últimas 24h
              </span>
            </div>
          </div>

          {/* Card 3: Média de Tempo para Abertura (24h) */}
          <div className="rounded-xl bg-[#1E293B] p-5 text-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#A7F3D0]">
                Média Tempo (24h)
              </span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-[#A7F3D0]">
                <Timer size={18} />
              </span>
            </div>
            <div className="mt-4">
              <strong className="block text-3xl font-bold leading-tight text-[#A7F3D0]">
                <AnimatedNumber
                  value={dashboardTotals.mediaTempo24h}
                  formatter={(val) => `${val.toFixed(1)}s`}
                />
              </strong>
              <span className="mt-2 block text-xs text-[#CBD5E1]">
                Tempo médio de resposta das cancelas nas últimas 24h
              </span>
            </div>
          </div>

          {/* Card 4: Total Abriu */}
          <div className="rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">
                Abriu
              </span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircle2 size={18} />
              </span>
            </div>
            <div className="mt-4">
              <strong className="block text-2xl font-bold text-[#17213D]">
                <AnimatedNumber value={dashboardTotals.totalConcluidos} />
              </strong>
              <span className="mt-1 block text-xs text-emerald-600 font-semibold">
                Retorno 200 OK da cancela
              </span>
            </div>
          </div>

          {/* Card 5: Total Não Abriu */}
          <div className="rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">
                Não Abriu
              </span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                <XCircle size={18} />
              </span>
            </div>
            <div className="mt-4">
              <strong className="block text-2xl font-bold text-[#17213D]">
                <AnimatedNumber value={dashboardTotals.totalFalhas} />
              </strong>
              <span className="mt-1 block text-xs text-rose-500 font-semibold">
                Retorno com erro ou sem resposta
              </span>
            </div>
          </div>
        </div>

        {/* Seção da Tabela com Switch de Botões (Abriu x Não Abriu) */}
        <div className="rounded-xl border border-[#E4EAF0] bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[#17213D]">Registros de Auditoria</h2>
              <p className="text-xs text-[#6B7280]">
                Alternar entre os registros onde a cancela abriu e não abriu.
              </p>
            </div>

            {/* Switch de 2 Botões (Padrão Caixa Geral) */}
            <div className="flex flex-wrap rounded-md border border-[#DDE3EA] bg-[#F8FAFC] p-1 gap-1">
              <button
                type="button"
                onClick={() => setActiveTab("abriu")}
                className={`h-9 rounded-md px-4 text-xs font-bold transition-all flex items-center gap-2 ${
                  activeTab === "abriu"
                    ? "bg-[#062D4E] text-white shadow-sm"
                    : "text-[#4B5563] hover:bg-white hover:text-[#17213D]"
                }`}
              >
                <CheckCircle2 size={14} />
                Abriu
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("nao_abriu")}
                className={`h-9 rounded-md px-4 text-xs font-bold transition-all flex items-center gap-2 ${
                  activeTab === "nao_abriu"
                    ? "bg-[#062D4E] text-white shadow-sm"
                    : "text-[#4B5563] hover:bg-white hover:text-[#17213D]"
                }`}
              >
                <XCircle size={14} />
                Não Abriu
              </button>
            </div>
          </div>

          {/* Grid de Dados */}
          <div className="flex-1 w-full min-h-0">
            <Grid
              ref={gridRef}
              columns={columns}
              filters={[]}
              pagination
              path={DATA_PATH}
              customFilters={customFilters}
              status={[]}
              onView={(data: IAuditoriaEntradaSaida) => {
                setSelectedRow(data);
                setIsView(true);
              }}
              customButtons={[
                {
                  label: "Conhecer",
                  icon: () => InfoIcon,
                  action: (data: IAuditoriaEntradaSaida) => {
                    addBreadcrumb("Conhecer");
                    setSelectedRow(data);
                    setIsView(true);
                  },
                  status: [],
                },
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EntradaVeiculosAudit;
